import React, { useEffect, useState } from "react";
import client from "../api";

/**
 * Google Business Profile card for the restaurateur dashboard.
 *
 * Hybrid flow (no Google Cloud API approval needed):
 *   1) Restaurateur enters their Google Business Profile data (business URL or Place ID)
 *      → saved on the restaurant model.
 *   2) Click "Collega Google Business":
 *      - The FoodGo storefront URL is copied to clipboard
 *      - A new tab opens directly on the Business Profile editor (or business.google.com fallback)
 *      - A clear 3-step instruction overlay appears so the user can paste the URL into the
 *        "Ordina online" field and click Save.
 *   3) When (and if) Renova later activates the OAuth API path, the "/google-business/status"
 *      endpoint will return `configured=true && connected=true` and a one-click automatic flow
 *      will be enabled in parallel.
 */
export default function GoogleBusinessCard({ rest, t }) {
  const [placeId, setPlaceId] = useState(rest.gbp_place_id || "");
  const [businessUrl, setBusinessUrl] = useState(rest.gbp_business_url || "");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const storefrontUrl = `${window.location.origin}/r/${rest.slug}`;

  useEffect(() => {
    setPlaceId(rest.gbp_place_id || "");
    setBusinessUrl(rest.gbp_business_url || "");
  }, [rest.id, rest.gbp_place_id, rest.gbp_business_url]);

  const saveAndConnect = async () => {
    setSaving(true);
    setFlash(null);
    try {
      // 1) Persist the GBP data the restaurateur typed in
      await client.put(`/restaurants/${rest.id}`, {
        gbp_place_id: (placeId || "").trim(),
        gbp_business_url: (businessUrl || "").trim(),
      });

      // 2) Copy FoodGo URL to clipboard (best-effort, works on https)
      let copied = false;
      try {
        await navigator.clipboard.writeText(storefrontUrl);
        copied = true;
      } catch {
        copied = false;
      }

      // 3) Build the most direct Business Profile editor URL we can
      const url = (businessUrl || "").trim();
      const pid = (placeId || "").trim();
      let target;
      if (pid) {
        // Direct deep-link to the Business Profile dashboard for that location
        target = `https://business.google.com/dashboard/l/${encodeURIComponent(pid)}`;
      } else if (url) {
        target = url;
      } else {
        target = "https://business.google.com/";
      }

      // 4) Open the new tab and show the guide overlay
      window.open(target, "_blank", "noopener,noreferrer");
      setShowGuide(true);
      setFlash({
        kind: "ok",
        msg: copied
          ? (t.gbp?.linkCopied || "Link FoodGo copiato! Incollalo nel campo 'Ordina online' su Google.")
          : (t.gbp?.linkCopyManual || "Apertura Google Business in corso. Copia manualmente il link mostrato sopra."),
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && detail.message) || detail || e.message;
      setFlash({ kind: "err", msg });
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(storefrontUrl);
      setFlash({ kind: "ok", msg: t.gbp?.linkCopied || "Link FoodGo copiato!" });
    } catch {
      setFlash({ kind: "err", msg: "Copia manuale richiesta." });
    }
  };

  return (
    <div className="card-neon p-5 mt-4" data-testid="gbp-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">Google Business</div>
          <h3 className="font-heading font-semibold text-lg mt-0.5">{t.gbp?.title || "Pubblica il link FoodGo su Google"}</h3>
          <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
            {t.gbp?.subtitle || "Inserisci i dati del tuo profilo Google Business e pubblica il pulsante 'Ordina online' con il link FoodGo del tuo ristorante."}
          </p>
        </div>
      </div>

      {flash && (
        <div className={`text-xs rounded-lg p-3 mb-3 border ${flash.kind === "ok" ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" : flash.kind === "warn" ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-red-500/40 bg-red-500/10 text-red-200"}`} data-testid="gbp-flash">
          {flash.msg}
        </div>
      )}

      {/* User inputs */}
      <div className="space-y-3 mb-4">
        <div>
          <label className="input-label" htmlFor="gbp-business-url">
            🌐 {t.gbp?.businessUrlLabel || "URL del tuo profilo Google Business"}
          </label>
          <input
            id="gbp-business-url"
            type="url"
            placeholder={t.gbp?.businessUrlPh || "Es. https://g.page/il-mio-ristorante o link della scheda Google Maps"}
            value={businessUrl}
            onChange={(e) => setBusinessUrl(e.target.value)}
            className="input-neon"
            data-testid="gbp-input-business-url"
          />
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {t.gbp?.businessUrlHint || "Apri Google Maps, cerca il tuo ristorante, clicca «Condividi» e copia il link."}
          </div>
        </div>
        <div>
          <label className="input-label" htmlFor="gbp-place-id">
            🆔 {t.gbp?.placeIdLabel || "Place ID Google (opzionale, più preciso)"}
          </label>
          <input
            id="gbp-place-id"
            type="text"
            placeholder="ChIJ..."
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            className="input-neon"
            data-testid="gbp-input-place-id"
          />
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {t.gbp?.placeIdHint || "Trovi il Place ID su"}{" "}
            <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="underline text-neon-cyan">Google Place ID finder</a>.
          </div>
        </div>
      </div>

      {/* FoodGo URL preview + copy */}
      <div className="rounded-xl border border-line bg-deep/40 p-3 mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-1.5">
          {t.gbp?.urlToPublish || "Link da incollare su Google"}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs text-neon-cyan font-mono break-all" data-testid="gbp-storefront">{storefrontUrl}</code>
          <button onClick={copyLink} className="btn-outline !py-1 !px-2 text-[10px]" data-testid="gbp-copy-btn">
            📋 {t.gbp?.copy || "Copia"}
          </button>
        </div>
      </div>

      {/* Primary action */}
      <button
        onClick={saveAndConnect}
        disabled={saving || (!placeId.trim() && !businessUrl.trim())}
        className="btn-primary !py-2 !px-4 text-xs"
        data-testid="gbp-connect-btn"
      >
        🔗 {saving ? "…" : (t.gbp?.connect || "Collega Google Business")}
      </button>
      {!placeId.trim() && !businessUrl.trim() && (
        <div className="text-[10px] text-amber-300 mt-2 font-mono">
          {t.gbp?.fillFirst || "Compila almeno uno dei campi qui sopra per abilitare il pulsante."}
        </div>
      )}

      {/* Step-by-step guide overlay (shown after click) */}
      {showGuide && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowGuide(false)} data-testid="gbp-guide-modal">
          <div className="bg-deep-card border border-line rounded-2xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">{t.gbp?.guideTitle || "Pubblica il link su Google Business"}</h3>
              <button onClick={() => setShowGuide(false)} className="text-zinc-400 hover:text-white" data-testid="gbp-guide-close">✕</button>
            </div>
            <p className="text-xs text-zinc-400 mb-4">
              {t.gbp?.guideIntro || "Abbiamo aperto Google Business in una nuova scheda e copiato il link FoodGo negli appunti. Segui questi 3 passi:"}
            </p>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-cyan text-[#06061a] flex items-center justify-center text-xs font-bold font-mono">1</span>
                <span className="text-zinc-200">{t.gbp?.guideStep1 || "Sulla scheda Google Business appena aperta, clicca «Modifica profilo» (o «Edit profile»)."}</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-cyan text-[#06061a] flex items-center justify-center text-xs font-bold font-mono">2</span>
                <span className="text-zinc-200">
                  {t.gbp?.guideStep2 || "Cerca la sezione «Ordini» (Orders) o «Link per ordinare» e incolla questo URL:"}
                  <div className="mt-1 p-2 rounded bg-deep border border-line">
                    <code className="text-neon-cyan text-[11px] font-mono break-all">{storefrontUrl}</code>
                  </div>
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-cyan text-[#06061a] flex items-center justify-center text-xs font-bold font-mono">3</span>
                <span className="text-zinc-200">{t.gbp?.guideStep3 || "Clicca «Salva» (o «Save»). Tra qualche minuto Google mostrerà il pulsante «Ordina online» sul tuo profilo."}</span>
              </li>
            </ol>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={copyLink} className="btn-outline !py-2 !px-3 text-xs" data-testid="gbp-guide-copy">
                📋 {t.gbp?.copyAgain || "Ricopia link"}
              </button>
              <button onClick={() => setShowGuide(false)} className="btn-primary !py-2 !px-4 text-xs" data-testid="gbp-guide-done">
                ✓ {t.gbp?.guideDone || "Fatto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
